import type { FieldWorkSource } from './fieldWorkRequest';
import type { FieldOpsTeam } from './fieldOpsTeam';

/**
 * The Field Ops directory, served by leatherback's
 * `Users.ListFieldOpsPeople` (`GET /v1/fieldOpsPeople`) -- see
 * `api/field-ops-people-api.ts`. Each entry is a staff member an admin has
 * tagged on `/field-work/settings` (in the `users` table) with a Field
 * Work `source` and/or `team`. Used to prefill a new request's Source/Team
 * from whoever is signed in; always still editable.
 */
export interface FieldOpsPerson {
  email: string;
  displayName: string;
  /** Prefilled into a new request's Source when this person opens it. */
  source?: FieldWorkSource | undefined;
  /** Prefilled into a new request's Team (before any site is chosen). */
  team?: FieldOpsTeam | undefined;
  /** Underlying leatherback User.version, for editing the tags. */
  version?: number | undefined;
}

function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Find the signed-in person in the directory: the verified email wins,
 * then a normalized display-name match (the directory's `email` is the
 * leatherback user id, but a Google display name is a useful fallback).
 */
export function resolveFieldOpsPerson(
  people: readonly FieldOpsPerson[],
  email: string | undefined,
  displayName?: string,
): FieldOpsPerson | undefined {
  if (email) {
    const target = email.trim().toLowerCase();
    const hit = people.find((person) => person.email.toLowerCase() === target);
    if (hit) return hit;
  }
  if (displayName) {
    const target = normalizeName(displayName);
    return people.find((person) => normalizeName(person.displayName) === target);
  }
  return undefined;
}
